import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { AppContext } from "App";
import {
  BUDGET_CATEGORY_DIRECT,
  BUDGET_CATEGORY_INDIRECT,
  BUDGET_COST_DEFAULTS,
  budgetLineItemLabel,
  budgetLineItemStoredName,
  getBudgetYears,
  UNIT_OF_MEASURE_NAMES,
} from "components/Budget/helpers";
import DropdownSelector from "components/DropdownSelector";
import { BudgetIcon } from "components/Icons/Icons";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import _ from "lodash";
import DeleteConfirmationButton from "mui-toolkit/DeleteConfirmationButton";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { formatPrice } from "utility/formatters";
import { v4 as uuidv4 } from "uuid";

import BudgetMasterGrid from "./BudgetMasterGrid";
import { planAdjustPrices, planCopyAllCosts, planCopyCost } from "./budgetMasterPlan";
import {
  applyBudgetMasterSave,
  buildBudgetMasterRows,
  buildCostRows,
  filterBudgetMasterRows,
  getAddScheduleItemSave,
  getCostChildren,
  getCostOptions,
  getCostsEditSave,
  getDeleteCostSave,
  getEditError,
  getEditSave,
  getFilterOptions,
  getScheduleSave,
  getUndoSave,
  isBudgetMasterCellEditable,
  isCostsCellEditable,
  isSameCost,
  masterKey,
  scheduleItemValue,
  sortBudgetMasterRows,
} from "./budgetMasterRows";
import { BudgetSchedulePopover } from "./BudgetSchedulePopover";
import { saveBudgetMaster, searchBudgetMaster } from "./BudgetService";
import { exportBudgetMasterWorkbook } from "./exportBudgetMasterWorkbook";

const NO_SCHEDULE = [];
// A read-only budget grant can't be seen up front; the API refuses the save with AuthenticationHelper's grant failure.
// ponytail: known only by its wording, so a reworded message shows as a plain error; an error code would be sturdier.
const GRANT_FAILURE = "Not authorized: check user tokens.";
const NO_EDIT_PERMISSION = "You don't have permission to edit this producer's budgets. Your change wasn't saved.";
const SCHEDULED = "Set by its schedule items";
const DEFAULT_COSTS = BUDGET_COST_DEFAULTS;
const NEW_COST = { category: "", name: "New cost…" };

const isMaster = (row) => row.kind === "master";
const isChild = (row) => !isMaster(row);
const rowLabel = (row) => `${row.budget_name} · ${row.countyLabel} · ${row.cropLabel}`;
const formatQuantity = (value, digits = 2) => Number(value).toLocaleString("en-US", { maximumFractionDigits: digits });
const formatMoney = (value) => formatPrice(value, 2);
const numberColumn = (format, isShown = isMaster) => ({
  type: "number",
  align: "right",
  headerAlign: "right",
  minWidth: 110,
  valueFormatter: (value, row) => (isShown(row) ? format(value) : ""),
});

const getBudgetMasterColumns = ({ onOpenPractice, onOpenSchedule, onDeleteCost, onCopyAllCosts }) => [
  {
    field: "budget_name",
    headerName: "Budget",
    minWidth: 140,
    flex: 1,
    // A cost row spans Budget through Acres, so its $/acre lines up under Cost/ac.
    colSpan: (value, row) => (isMaster(row) ? 1 : 4),
    renderCell: ({ row }) =>
      isMaster(row) ? (
        row.budget_name
      ) : (
        <Box sx={{ pl: 3 }}>
          {budgetLineItemLabel(row.name)}{" "}
          <Typography component="span" variant="body2" color="text.secondary">
            {row.category}
          </Typography>
        </Box>
      ),
  },
  { field: "countyLabel", headerName: "County", minWidth: 140 },
  { field: "cropLabel", headerName: "Crop", minWidth: 160, flex: 1 },
  { field: "reported_acreage", headerName: "Acres", editable: true, ...numberColumn(formatQuantity) },
  {
    field: "costPerAcre",
    headerName: "Cost/ac",
    editable: true,
    ...numberColumn(formatMoney),
    // A cost row shows, and edits, its own $/acre in this column.
    valueGetter: (value, row) => (isMaster(row) ? value : row.value),
    valueSetter: (value, row) => (isMaster(row) ? { ...row, costPerAcre: value } : { ...row, value }),
    renderCell: ({ row, formattedValue, tabIndex }) => {
      if (isMaster(row)) return formattedValue;
      if (!row.isScheduled) return formatMoney(row.value);
      return (
        <Button
          size="small"
          tabIndex={tabIndex}
          startIcon={<FormatListBulletedIcon titleAccess={SCHEDULED} />}
          aria-label={`${row.name} schedule, ${formatMoney(row.value)}`}
          onClick={(event) => onOpenSchedule(event, row)}
        >
          {formatMoney(row.value)}
        </Button>
      );
    },
  },
  {
    field: "actions",
    type: "actions",
    headerName: "",
    width: 100,
    getActions: ({ row }) =>
      isMaster(row)
        ? [
            <GridActionsCellItem
              key="copy"
              icon={<ContentCopyOutlinedIcon />}
              label="Copy all costs to…"
              onClick={() => onCopyAllCosts(row)}
            />,
            <GridActionsCellItem
              key="open"
              icon={<EastOutlinedIcon />}
              label="Open county/crop budget"
              onClick={() => onOpenPractice(row)}
            />,
          ]
        : [<DeleteConfirmationButton key="delete" item={row} onChange={onDeleteCost} />],
  },
];

const getCostsColumns = ({ onAddScheduleItem, onDeleteScheduleItem, onCopyCost, costNames, canAddItem }) => {
  const columns = [
    {
      field: "budget_name",
      headerName: "Budget",
      editable: true,
      type: "singleSelect",
      valueOptions: costNames,
      minWidth: 140,
      flex: 1,
      // An item's name spans Budget through Acres, so its quantity, unit, and price sit under their own headers.
      colSpan: (value, row) => (isMaster(row) ? 1 : row.isAddItem ? columns.length : 4),
      valueGetter: (value, row) => (isMaster(row) ? value : row.name),
      valueSetter: (value, row) => (isMaster(row) ? { ...row, budget_name: value } : { ...row, name: value }),
      renderCell: ({ row, formattedValue, tabIndex }) => {
        if (isMaster(row)) {
          return (
            <Box>
              {row.budget_name}{" "}
              <Typography component="span" variant="body2" color="text.secondary">
                {budgetLineItemLabel(row.cost.name)}
              </Typography>
            </Box>
          );
        }
        if (row.isAddItem) {
          return (
            <Button
              size="small"
              tabIndex={tabIndex}
              startIcon={<AddCircleOutlineIcon />}
              disabled={!canAddItem}
              onClick={() => onAddScheduleItem(row.parent)}
              sx={{ ml: 2 }}
            >
              Add item
            </Button>
          );
        }
        return (
          <Box sx={{ pl: 3 }}>
            {row.name || (
              <Typography component="span" variant="body2" color="text.secondary">
                Name
              </Typography>
            )}
          </Box>
        );
      },
    },
    { field: "countyLabel", headerName: "County", minWidth: 140 },
    { field: "cropLabel", headerName: "Crop", minWidth: 160, flex: 1 },
    { field: "reported_acreage", headerName: "Acres", ...numberColumn(formatQuantity) },
    {
      field: "quantity",
      headerName: "Qty",
      editable: true,
      sortable: false,
      ...numberColumn((value) => formatQuantity(value, 4), isChild),
    },
    {
      field: "unit_of_measure",
      headerName: "Unit",
      type: "singleSelect",
      valueOptions: UNIT_OF_MEASURE_NAMES,
      editable: true,
      sortable: false,
      minWidth: 90,
    },
    {
      field: "unit_value",
      headerName: "Unit price",
      editable: true,
      sortable: false,
      ...numberColumn((value) => formatPrice(value, 4), isChild),
    },
    {
      field: "costValue",
      headerName: "$/acre",
      editable: true,
      ...numberColumn(formatMoney),
      valueGetter: (value, row) => (isMaster(row) ? row.costValue : scheduleItemValue(row)),
      valueFormatter: (value) => (value == null ? "—" : formatMoney(value)),
      renderCell: ({ row, formattedValue }) => {
        if (!isMaster(row)) return formattedValue;
        if (row.costValue == null) return "—";
        return row.cost.isScheduled ? (
          <span>
            <span role="img" aria-label={SCHEDULED} title={SCHEDULED}>
              ≡
            </span>{" "}
            {formatMoney(row.costValue)}
          </span>
        ) : (
          formatMoney(row.costValue)
        );
      },
    },
    {
      field: "costTotal",
      headerName: "$ total",
      ...numberColumn((value) => (value === null ? "" : formatMoney(value))),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      width: 60,
      getActions: ({ row }) => {
        if (isChild(row)) return [<DeleteConfirmationButton key="delete" item={row} onChange={onDeleteScheduleItem} />];
        // A row without the cost (no id yet) has nothing to copy.
        return row.cost.id
          ? [
              <GridActionsCellItem
                key="copy"
                icon={<ContentCopyOutlinedIcon />}
                label="Copy to…"
                onClick={() => onCopyCost(row)}
              />,
            ]
          : [];
      },
    },
  ];
  return columns;
};

const MODES = {
  master: {
    title: "Budget Master",
    getChildren: (row) => row.lineItems,
    getColumns: getBudgetMasterColumns,
    isCellEditable: isBudgetMasterCellEditable,
    getSave: getEditSave,
  },
  costs: {
    title: "Costs",
    getChildren: getCostChildren,
    getColumns: getCostsColumns,
    isCellEditable: isCostsCellEditable,
    getSave: getCostsEditSave,
  },
};

function NewCostDialog({ onClose, onCreate }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState(BUDGET_CATEGORY_DIRECT);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New cost</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          // BudgetCountyPracticeLineItem.name is varchar(50).
          inputProps={{ maxLength: 50 }}
        />
        <RadioGroup row value={category} onChange={(event) => setCategory(event.target.value)}>
          <FormControlLabel value={BUDGET_CATEGORY_DIRECT} control={<Radio />} label="Direct" />
          <FormControlLabel value={BUDGET_CATEGORY_INDIRECT} control={<Radio />} label="Indirect" />
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim()} onClick={() => onCreate({ category, name: name.trim() })}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Adjust prices…: which price on `rows` to change, and how. The plan's confirm follows. */
function AdjustPricesDialog({ cost, rows, isChecked, onClose, onPreview }) {
  // An item name in any case is one choice; other spellings are choices of their own.
  const itemNames = useMemo(
    () =>
      _.uniqBy(rows.flatMap((row) => row.cost.scheduleItems.map((si) => si.name)).filter(Boolean), (name) =>
        name.toLowerCase()
      ).sort((a, b) => a.localeCompare(b)),
    [rows]
  );
  // "" is the cost's $/acre; item names here are never blank.
  const [itemName, setItemName] = useState("");
  const [op, setOp] = useState("set");
  const [amount, setAmount] = useState("");

  const value = amount === "" ? NaN : Number(amount);
  let error = null;
  if (!Number.isFinite(value)) error = "Enter a number.";
  else if (op === "set" && value < 0) error = "Enter $0 or more.";
  else if (op === "pct" && value <= -100) error = "Enter more than -100%.";

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Adjust {budgetLineItemLabel(cost.name)} prices</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          {isChecked
            ? `Applies to the ${rows.length} checked ${rows.length === 1 ? "row" : "rows"}.`
            : `Applies to all ${rows.length} ${rows.length === 1 ? "row" : "rows"} that match the filters.`}
        </DialogContentText>
        <TextField
          select
          fullWidth
          margin="dense"
          label="Price"
          value={itemName}
          onChange={(event) => setItemName(event.target.value)}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">{budgetLineItemLabel(cost.name)} $/acre</MenuItem>
          {itemNames.map((name) => (
            <MenuItem key={name} value={name}>
              {name} unit price
            </MenuItem>
          ))}
        </TextField>
        <RadioGroup row value={op} onChange={(event) => setOp(event.target.value)}>
          <FormControlLabel value="set" control={<Radio />} label="Set to $" />
          <FormControlLabel value="pct" control={<Radio />} label="Change by ±%" />
        </RadioGroup>
        <TextField
          fullWidth
          margin="dense"
          type="number"
          label={op === "set" ? "Amount ($)" : "Amount (%)"}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          error={amount !== "" && Boolean(error)}
          helperText={amount !== "" && error}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={Boolean(error)}
          onClick={() => onPreview({ target: itemName ? { itemName } : "cost", op, amount: value })}
        >
          Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** A plan's before/after $/acre for each row it changes (budgetMasterPlan.js). Cancel changes nothing. */
function PlanConfirmDialog({ title, children, preview, onConfirm, onClose }) {
  const [isSaving, setIsSaving] = useState(false);
  const hasRemoved = preview.some((row) => row.removed);

  const handleConfirm = async () => {
    setIsSaving(true);
    await onConfirm();
    setIsSaving(false);
  };

  return (
    <Dialog open onClose={isSaving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {children}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Budget</TableCell>
              <TableCell>County</TableCell>
              <TableCell>Crop</TableCell>
              <TableCell align="right">Before $/acre</TableCell>
              <TableCell align="right">After $/acre</TableCell>
              {hasRemoved && <TableCell>Costs removed</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {preview.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.budget_name}</TableCell>
                <TableCell>{row.countyLabel}</TableCell>
                <TableCell>{row.cropLabel}</TableCell>
                <TableCell align="right">{row.before === null ? "—" : formatMoney(row.before)}</TableCell>
                <TableCell align="right">{formatMoney(row.after)}</TableCell>
                {hasRemoved && <TableCell>{row.removed.join(", ") || "—"}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={isSaving}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MultiSelect({ label, options, value, onChange }) {
  return (
    <Autocomplete
      multiple
      limitTags={2}
      options={options}
      value={value}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
      isOptionEqualToValue={(a, b) => (a?.id && b?.id ? a.id === b.id : a === b)}
      onChange={(event, selected) => onChange(selected)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length ? undefined : "All"}
          InputLabelProps={{ shrink: true }}
        />
      )}
      sx={{ width: { xs: "100%", md: 260 } }}
    />
  );
}

export default function BudgetMasterWorkspace({ mode = "master" }) {
  const { title, getChildren: modeGetChildren, getColumns, isCellEditable, getSave } = MODES[mode];
  const isCosts = mode === "costs";
  const { globalState, globalDispatch } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { component: formStatusComponent, actions: formStatusActions } = useFormStatus();

  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);
  const [response, setResponse] = useState(null);
  // Unfiltered year dump: filter dropdowns stay complete while the grid refetches on Budget/County/Crop.
  const [catalog, setCatalog] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filter, setFilter] = useState({ budgetIds: [], counties: [], crops: [], cost: null });
  const budgetDefaultKey = `${globalState?.producer_token ?? ""}:${currentYear ?? ""}`;
  const appliedBudgetDefault = useRef("");
  const [sortModel, setSortModel] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [isNewCostOpen, setIsNewCostOpen] = useState(false);
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [pendingPlan, setPendingPlan] = useState(null);
  const isAddingItem = useRef(false);
  // Callbacks memoized for the grid's columns and the schedule popover read the latest response and checked rows here.
  const responseRef = useRef(response);
  const checkedRowsRef = useRef([]);
  useEffect(() => {
    responseRef.current = response;
  }, [response]);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const applyLocalSave = (save) => {
    setResponse((previous) => applyBudgetMasterSave(previous, save));
    setCatalog((previous) => (previous ? applyBudgetMasterSave(previous, save) : previous));
  };

  /** Saves through BudgetMaster_Update behind the saving indicator. Reports a failure and returns false. */
  const save = async (budgetMasterSave) => {
    formStatusActions.setIsFormDirty(true, true);
    try {
      await saveBudgetMaster(globalState, budgetMasterSave);
      formStatusActions.setIsSaving(false);
      return true;
    } catch (err) {
      formStatusActions.setIsFormDirty(true, false);
      if (err?.detail !== GRANT_FAILURE) handleError(err);
      else enqueueSnackbar(NO_EDIT_PERMISSION, { preventDuplicate: true, variant: "error" });
      return false;
    }
  };

  // Inline edits show at once and revert if the save fails.
  const processRowUpdate = async (row, oldRow) => {
    const edit = getSave(row, oldRow, uuidv4);
    if (!edit) return oldRow;
    const error = getEditError(edit);
    if (error) {
      enqueueSnackbar(error, { preventDuplicate: true, variant: "warning" });
      return oldRow;
    }

    const undo = getUndoSave(response, edit);
    applyLocalSave(edit);
    const isSaved = await save(edit);
    // The grid merges the returned row into its cache. This later render replaces that with rows built from state,
    // which carry the new totals and sort order.
    applyLocalSave(isSaved ? edit : undo);
    return isSaved ? row : oldRow;
  };

  const handleDeleteCost = useCallback(
    async (lineItem) => {
      const deleteSave = getDeleteCostSave(lineItem);
      if (await save(deleteSave)) applyLocalSave(deleteSave);
    },
    [globalState]
  );

  const handleScheduleChange = useCallback(
    async (scheduleItems) => {
      const scheduleSave = getScheduleSave(responseRef.current, scheduleItems);
      if (await save(scheduleSave)) applyLocalSave(scheduleSave);
    },
    [globalState]
  );

  const handleAddScheduleItem = useCallback(
    async (row) => {
      if (!filter.cost) return;
      // On a row without the cost, a second click before the first save lands would create the cost twice.
      // ponytail: one lock for the whole grid, so it's one add at a time and a click on another row meanwhile is
      // dropped; lock per row if that bites.
      if (isAddingItem.current) return;
      isAddingItem.current = true;
      const addSave = getAddScheduleItemSave(row, uuidv4);
      if (await save(addSave)) applyLocalSave(addSave);
      isAddingItem.current = false;
    },
    [globalState, filter.cost]
  );

  /** Opens the confirm for a bulk action's plan, or says `empty` when the plan changes no row. True when it opened. */
  const confirmPlan = (plan, { empty, title, prompt }) => {
    const count = plan.preview.length;
    if (count) setPendingPlan({ title: title(`${count} ${count === 1 ? "row" : "rows"}`), prompt, plan });
    else enqueueSnackbar(empty, { preventDuplicate: true, variant: "info" });
    return count > 0;
  };

  const handleCopyAllCosts = useCallback(
    (row) =>
      confirmPlan(planCopyAllCosts(row, checkedRowsRef.current, uuidv4), {
        empty: "Check the rows to copy all costs to, then choose Copy all costs to… again.",
        title: (rows) => `Copy all costs to ${rows}?`,
        prompt: `Each row's costs and their items are replaced by those in ${rowLabel(row)}. The row's other costs are removed.`,
      }),
    [enqueueSnackbar]
  );

  const handleCopyCost = useCallback(
    (row) => {
      const name = budgetLineItemLabel(row.cost.name);
      confirmPlan(planCopyCost(row, checkedRowsRef.current, row.cost, uuidv4), {
        empty: `Check the rows to copy ${name} to, then choose Copy to… again.`,
        title: (rows) => `Copy ${name} to ${rows}?`,
        prompt: `Each row's ${name} and its items are replaced by those in ${rowLabel(row)}. Rows without ${name} get it.`,
      });
    },
    [enqueueSnackbar]
  );

  /** One all-or-nothing save; after a failure the screen is as it was. */
  const handleConfirmPlan = async () => {
    const planSave = pendingPlan.plan.save;
    if (!(await save(planSave))) return;
    applyLocalSave(planSave);
    setRowSelectionModel([]);
    setPendingPlan(null);
  };

  useEffect(() => {
    globalDispatch({ type: "update", payload: { key: "crop_year", value: currentYear } });
  }, [currentYear]);

  const filterQueryKey = [filter.budgetIds.join(), filter.counties.join(), filter.crops.join()].join("|");
  useEffect(() => {
    appliedBudgetDefault.current = "";
    setCatalog(null);
    setFilter((prev) => ({ ...prev, budgetIds: [], counties: [], crops: [] }));
  }, [budgetDefaultKey]);
  useEffect(() => {
    // An agent with no producer selected has nothing to read (and the API returns 400).
    if (!currentYear || !globalState?.producer_token) {
      setResponse(null);
      setCatalog(null);
      setIsLoading(false);
      return;
    }

    let isCurrent = true;
    setIsLoading(true);
    const filters = { budgetIds: filter.budgetIds, counties: filter.counties, crops: filter.crops };
    searchBudgetMaster(globalState, currentYear, filters)
      .then((loaded) => {
        if (!isCurrent) return;
        setResponse(loaded);
        if (!filters.budgetIds.length && !filters.counties.length && !filters.crops.length) setCatalog(loaded);
      })
      .catch((err) => {
        if (!isCurrent) return;
        setResponse(null);
        handleError(err);
      })
      .finally(() => isCurrent && setIsLoading(false));
    return () => {
      isCurrent = false;
    };
  }, [globalState?.producer_token, currentYear, filterQueryKey]);

  const rows = useMemo(() => buildBudgetMasterRows(response), [response]);
  const catalogRows = useMemo(() => buildBudgetMasterRows(catalog ?? response), [catalog, response]);
  const options = useMemo(() => getFilterOptions(catalogRows), [catalogRows]);
  useEffect(() => {
    if (!catalog || appliedBudgetDefault.current === budgetDefaultKey) return;
    appliedBudgetDefault.current = budgetDefaultKey;
    if (options.activeBudgetIds.length)
      setFilter((prev) => ({ ...prev, budgetIds: options.activeBudgetIds, counties: [], crops: [] }));
  }, [budgetDefaultKey, catalog, options.activeBudgetIds]);
  const { cost } = filter;
  const costOptions = useMemo(() => getCostOptions(catalogRows, DEFAULT_COSTS, cost), [catalogRows, cost]);
  const costNames = useMemo(
    () =>
      _.uniq([
        ...costOptions.map((option) => budgetLineItemLabel(option.name)),
        ...catalogRows.flatMap((row) =>
          row.lineItems.flatMap((li) => li.scheduleItems.map((si) => si.name))
        ),
      ]).filter(Boolean),
    [costOptions, catalogRows]
  );
  const getChildren = useCallback(
    isCosts ? getCostChildren : modeGetChildren,
    [isCosts, modeGetChildren]
  );
  const visibleRows = useMemo(() => {
    const filteredRows = filterBudgetMasterRows(rows, filter);
    return sortBudgetMasterRows(isCosts ? buildCostRows(filteredRows, cost) : filteredRows, sortModel[0]);
  }, [rows, filter, isCosts, cost, sortModel]);
  // Bulk actions apply to the checked rows that pass the filters, on any page. A row the filters hide is unchecked.
  const checkedRows = useMemo(() => {
    const checked = new Set(rowSelectionModel);
    return visibleRows.filter((row) => checked.has(`master:${masterKey(row)}`));
  }, [visibleRows, rowSelectionModel]);
  useEffect(() => {
    checkedRowsRef.current = checkedRows;
    setRowSelectionModel((selection) =>
      selection.length === checkedRows.length ? selection : checkedRows.map((row) => `master:${masterKey(row)}`)
    );
  }, [checkedRows]);
  const selectedBudgets = options.budgets.filter((budget) => filter.budgetIds.includes(budget.id));

  const columns = useMemo(
    () =>
      getColumns({
        onOpenPractice: (row) => navigate(`/budget/${row.budget_id}/${row.id}`),
        onOpenSchedule: (event, lineItem) => setSchedule({ anchorEl: event.currentTarget, lineItem }),
        onDeleteCost: handleDeleteCost,
        onCopyAllCosts: handleCopyAllCosts,
        onAddScheduleItem: handleAddScheduleItem,
        onDeleteScheduleItem: (scheduleItem) => handleScheduleChange([scheduleItem]),
        onCopyCost: handleCopyCost,
        costNames,
        canAddItem: Boolean(cost),
      }),
    [
      getColumns,
      navigate,
      handleDeleteCost,
      handleCopyAllCosts,
      handleAddScheduleItem,
      handleScheduleChange,
      costNames,
      cost,
      handleCopyCost,
    ]
  );

  const handleNewCost = (newCost) => {
    setIsNewCostOpen(false);
    setFilter((prev) => ({ ...prev, cost: costOptions.find((option) => isSameCost(option, newCost)) ?? newCost }));
  };

  const adjustRows = checkedRows.length ? checkedRows : visibleRows;
  const handleAdjustPrices = (adjustment) => {
    const { target, op, amount } = adjustment;
    const price = target === "cost" ? `${budgetLineItemLabel(cost.name)} $/acre` : `${target.itemName} unit price`;
    const change =
      op === "set"
        ? `set to ${target === "cost" ? formatMoney(amount) : formatPrice(amount, 4)}`
        : `changed by ${amount}%`;
    const isOpened = confirmPlan(planAdjustPrices(adjustRows, adjustment, cost, uuidv4), {
      empty: "Nothing would change on these rows.",
      title: (rows) => `Adjust ${budgetLineItemLabel(cost.name)} on ${rows}?`,
      prompt: `${price} ${change}. Only the rows that change are listed.`,
    });
    if (isOpened) setIsAdjustOpen(false);
  };

  const handleExportClick = async () => {
    setIsExporting(true);
    try {
      await exportBudgetMasterWorkbook({
        rows: visibleRows,
        year: currentYear,
        budgets: selectedBudgets.map((budget) => budget.label),
        counties: filter.counties,
        crops: filter.crops,
        cost: isCosts ? cost : null,
      });
    } catch (err) {
      handleError(err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <WorkspaceHeader title={title} category={{ title: "Budgets", icon: <BudgetIcon /> }} />

      <Stack
        className="headerWithMenuButton"
        sx={{ flexDirection: { xs: "column", md: "row" }, alignItems: "start", gap: 2, mb: 2 }}
      >
        <Box sx={{ paddingTop: { md: "1.75rem" } }}>
          <DropdownSelector value={currentYear} onChange={setCurrentYear} options={getBudgetYears()} />
        </Box>
        <MultiSelect
          label="Budget"
          options={options.budgets}
          value={selectedBudgets}
          onChange={(selected) => setFilter((prev) => ({ ...prev, budgetIds: selected.map((budget) => budget.id) }))}
        />
        <MultiSelect
          label="County"
          options={options.counties}
          value={filter.counties}
          onChange={(counties) => setFilter((prev) => ({ ...prev, counties }))}
        />
        <MultiSelect
          label="Crop"
          options={options.crops}
          value={filter.crops}
          onChange={(crops) => setFilter((prev) => ({ ...prev, crops }))}
        />
        {isCosts && (
          <Autocomplete
            options={[...costOptions, NEW_COST]}
            value={cost}
            onChange={(event, option) =>
              option === NEW_COST ? setIsNewCostOpen(true) : setFilter((prev) => ({ ...prev, cost: option }))
            }
            getOptionLabel={(option) => budgetLineItemLabel(option?.name)}
            isOptionEqualToValue={isSameCost}
            renderInput={(params) => (
              <TextField {...params} label="Cost" placeholder={cost ? undefined : "All"} InputLabelProps={{ shrink: true }} />
            )}
            sx={{ width: { xs: "100%", md: 260 } }}
          />
        )}
        {isLoading && (
          <Box sx={{ paddingTop: { md: "1.75rem" } }}>
            <CircularProgress />
          </Box>
        )}
        {isCosts && (
          <Box sx={{ paddingTop: { md: "1.75rem" } }}>
            <Button
              variant="outlined"
              onClick={() => setIsAdjustOpen(true)}
              disabled={isLoading || !cost || !visibleRows.length}
              sx={{ whiteSpace: "nowrap" }}
            >
              Adjust prices…
            </Button>
          </Box>
        )}
        <Box sx={{ paddingTop: { md: "1.75rem" } }}>
          <Button
            variant="outlined"
            onClick={handleExportClick}
            disabled={isLoading || isExporting || !visibleRows.length}
            startIcon={isExporting ? <CircularProgress size={18} /> : <CloudDownloadOutlinedIcon />}
            sx={{ whiteSpace: "nowrap" }}
          >
            {isExporting ? "Exporting…" : "Export Excel"}
          </Button>
        </Box>
        <Box sx={{ paddingTop: { md: "1.75rem" } }}>{formStatusComponent}</Box>
      </Stack>

      <BudgetMasterGrid
        masters={visibleRows}
        getChildren={getChildren}
        columns={columns}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        isLoading={isLoading}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        isCellEditable={isCellEditable}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleError}
      />

      {/* The popover drops its edits when budgetSchedule changes identity, so it keeps the cost as it was opened. */}
      <BudgetSchedulePopover
        isPopoverOpen={Boolean(schedule)}
        anchorEl={schedule?.anchorEl}
        handlePopoverClose={() => setSchedule(null)}
        budgetSchedule={schedule?.lineItem.scheduleItems ?? NO_SCHEDULE}
        onChange={handleScheduleChange}
        selectedField={schedule?.lineItem}
        budgetCountyPracticeId={schedule?.lineItem.parent.id}
      />

      {isNewCostOpen && <NewCostDialog onClose={() => setIsNewCostOpen(false)} onCreate={handleNewCost} />}

      {isAdjustOpen && (
        <AdjustPricesDialog
          cost={cost}
          rows={adjustRows}
          isChecked={checkedRows.length > 0}
          onClose={() => setIsAdjustOpen(false)}
          onPreview={handleAdjustPrices}
        />
      )}

      {pendingPlan && (
        <PlanConfirmDialog
          title={pendingPlan.title}
          preview={pendingPlan.plan.preview}
          onConfirm={handleConfirmPlan}
          onClose={() => setPendingPlan(null)}
        >
          <DialogContentText sx={{ mb: 2 }}>{pendingPlan.prompt}</DialogContentText>
        </PlanConfirmDialog>
      )}
    </>
  );
}
